import React from 'react';
import { Link } from '@tanstack/react-router';
import {
  FiCalendar,
  FiMapPin,
  FiArrowRight,
  FiMusic,
  FiLock,
  FiExternalLink,
} from 'react-icons/fi';
import {
  type EventItem,
  getEventCoverImage,
  getEventLocation,
  isEventExpired,
  isEventCancelled,
} from '../../api/events';

interface EventsTabProps {
  events: EventItem[];
  isLoading?: boolean;
  isLoadingMore?: boolean;
  isOwnProfile?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatEventDate(iso?: string): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}

// ─── Main tab component ───────────────────────────────────────────────────────

export const EventsTab: React.FC<EventsTabProps> = ({
  events,
  isLoading,
  isLoadingMore,
  isOwnProfile = false,
  hasMore,
  onLoadMore,
}) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4 md:p-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-56 rounded-2xl bg-zinc-900 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!events || events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
        <div className="w-16 h-16 rounded-full bg-zinc-900 flex items-center justify-center text-zinc-600">
          <FiCalendar size={28} />
        </div>
        <div>
          <p className="text-white text-lg font-semibold">No Events Yet</p>
          <p className="text-zinc-500 text-sm mt-1">No events have been created yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4 md:p-6">
        {events.map((event) => (
          <EventCard key={event.id} event={event} isOwnProfile={isOwnProfile} />
        ))}
      </div>

      {/* Load More */}
      {(hasMore || isLoadingMore) && (
        <div className="flex justify-center pb-8">
          <button
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="px-6 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 border border-white/8 text-zinc-300 text-[13px] font-medium transition-colors"
          >
            {isLoadingMore ? 'Loading…' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Event card ───────────────────────────────────────────────────────────────

function EventCard({ event, isOwnProfile }: { event: EventItem; isOwnProfile: boolean }) {
  const cover = getEventCoverImage(event);
  const location = getEventLocation(event);
  const date = formatEventDate(event.start);
  const title = event.event_title || 'Untitled Event';

  const now = new Date();
  const expired = isEventExpired(event, now);
  const cancelled = isEventCancelled(event);
  const isExternal =
    event.event_ticketing_type === 'external' && Boolean(event.external_ticketing_url);

  // Dim cancelled/expired cards only on own profile (where they are shown)
  const dimmed = isOwnProfile && (expired || cancelled);

  const cardCls = `no-underline block rounded-2xl border transition-all duration-300 group overflow-hidden ${
    dimmed
      ? 'bg-zinc-900/40 border-white/5 opacity-70'
      : 'bg-zinc-900/60 border-white/8 hover:border-white/15 hover:bg-zinc-900'
  }`;

  return (
    <Link
      to="/events/$eventId"
      params={{ eventId: event.event_uuid ?? String(event.id) }}
      className={cardCls}
    >
      <EventCardInner
        event={event}
        date={date}
        cover={cover}
        location={location}
        title={title}
        expired={expired}
        cancelled={cancelled}
        dimmed={dimmed}
        isExternal={isExternal}
      />
    </Link>
  );
}

// ─── Shared inner content ─────────────────────────────────────────────────────

interface InnerProps {
  event: EventItem;
  date: string | null;
  cover: string | null;
  location: string | null;
  title: string;
  expired: boolean;
  cancelled: boolean;
  dimmed: boolean;
  isExternal: boolean;
}

function EventCardInner({
  event,
  date,
  cover,
  location,
  title,
  expired,
  cancelled,
  dimmed,
  isExternal,
}: InnerProps) {
  return (
    <>
      {/* Cover image */}
      <div className="relative w-full aspect-video bg-zinc-800 overflow-hidden">
        {cover ? (
          <img
            src={cover}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-700">
            <FiMusic size={36} />
          </div>
        )}

        {/* Status badges — top-left stack */}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {cancelled && (
            <span className="px-2.5 py-1 rounded-full bg-red-600/90 text-white text-[11px] font-semibold backdrop-blur-sm">
              Cancelled
            </span>
          )}
          {!cancelled && expired && (
            <span className="px-2.5 py-1 rounded-full bg-zinc-600/90 text-zinc-200 text-[11px] font-semibold backdrop-blur-sm">
              Expired
            </span>
          )}
        </div>

        {/* Private badge — top-right */}
        {event.is_private && (
          <div className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-zinc-300 backdrop-blur-sm">
            <FiLock size={11} />
          </div>
        )}

        {/* Date overlay */}
        {date && (
          <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-linear-to-t from-black/80 to-transparent">
            <div className="flex items-center gap-1.5 text-white text-[12px]">
              <FiCalendar size={11} />
              <span>{date}</span>
            </div>
          </div>
        )}
      </div>

      {/* Card body */}
      <div className="p-4 flex flex-col gap-2">
        <h3
          className={`font-semibold text-[15px] leading-snug transition-colors line-clamp-2 ${
            cancelled
              ? 'text-zinc-500 line-through'
              : dimmed
                ? 'text-zinc-400'
                : 'text-blue-400 group-hover:text-blue-300'
          }`}
        >
          {title}
        </h3>

        {location && (
          <div className="flex items-center gap-1.5 text-zinc-400 text-[12px]">
            <FiMapPin size={11} className="text-zinc-500 shrink-0" />
            <span className="truncate">{location}</span>
          </div>
        )}

        {/* Badges row */}
        <div className="flex flex-wrap gap-1.5 mt-0.5">
          {/* Single Event or Multi Event badge */}
          {event.event_structure_type === 'single' && (
            <span className="px-2.5 py-1 rounded-full bg-indigo-900/60 border border-indigo-700/40 text-indigo-300 text-[11px] font-medium">
              Single Event
            </span>
          )}
          {event.event_structure_type === 'multi_event' && (
            <span className="px-2.5 py-1 rounded-full bg-blue-900/60 border border-blue-700/40 text-blue-300 text-[11px] font-medium">
              Multi Event
            </span>
          )}

          {/* For multi events: show Main or Sub Event */}
          {event.event_structure_type === 'multi_event' && event.is_sub_event === false && (
            <span className="px-2.5 py-1 rounded-full bg-emerald-900/40 border border-emerald-700/30 text-emerald-300 text-[11px] font-medium">
              Main Event
            </span>
          )}
          {event.event_structure_type === 'multi_event' && event.is_sub_event === true && (
            <span className="px-2.5 py-1 rounded-full bg-purple-900/40 border border-purple-700/30 text-purple-300 text-[11px] font-medium">
              Sub Event
            </span>
          )}

          {/* Private badge */}
          {event.is_private && (
            <span className="px-2.5 py-1 rounded-full bg-zinc-800 border border-white/8 text-zinc-300 text-[11px] font-medium flex items-center gap-1">
              <FiLock size={10} />
              Private
            </span>
          )}

          {/* External badge */}
          {isExternal && (
            <span className="px-2.5 py-1 rounded-full bg-amber-900/50 border border-amber-700/40 text-amber-300 text-[11px] font-medium flex items-center gap-1">
              <FiExternalLink size={10} />
              External
            </span>
          )}

          {/* Additional event type badge */}
          {event.event_type && event.event_type.toLowerCase() !== 'physical' && (
            <span className="px-2.5 py-1 rounded-full bg-zinc-800 border border-white/8 text-zinc-300 text-[11px] font-medium capitalize">
              {event.event_type}
            </span>
          )}
        </div>

        {/* Footer row */}
        <div className="flex items-center justify-between mt-1">
          <div className="flex items-center gap-1 text-zinc-500 text-[12px] group-hover:text-white transition-colors">
            <span>View Details</span>
            <FiArrowRight size={12} />
          </div>
        </div>
      </div>
    </>
  );
}
